import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalComponent, ModalData, ModalTitleIcons } from '@modeso/aumico-lib-core-fe';
import { AccountType, IAccount } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, first } from 'rxjs';
import { localizationKeys } from '../../../constants/localizationKeys';
import { FliInEditModeService } from '../../../services/fli-in-edit-mode.service';

@UntilDestroy()
@Component({
    selector: 'aumico-account-form',
    templateUrl: './account-form.component.html',
    styleUrls: ['./account-form.component.scss'],
})
export class AccountFormComponent implements OnInit {
    accountForm: FormGroup;
    localizationKeys = localizationKeys;
    AccountType = AccountType;
    @Input() account: IAccount;
    @Input() hasPreviousData: boolean;
    @Input() parentName: string;
    @Output() saveAccount = new EventEmitter<IAccount>();
    @Output() cancelAccountEdit = new EventEmitter<void>();
    @Output() cancelAccountCreation = new EventEmitter<void>();
    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly domSanitizer: DomSanitizer,
        public readonly dialog: MatDialog,

        private readonly fliEditModeService: FliInEditModeService,
    ) {
        this.matIconRegistry.addSvgIcon(
            'save',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/aumico-pattern-icon-save.svg'),
        );
        this.matIconRegistry.addSvgIcon(
            'close',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/aumico-pattern-icon-cancel-gray.svg'),
        );
    }
    ngOnInit(): void {
        this.fliEditModeService.fliInEditMode$
            .pipe(
                filter((fliName) => fliName != null),
                untilDestroyed(this),
            )
            .subscribe((fliName) => {
                if (fliName === this.parentName) {
                    setTimeout(() => this.onCancel());
                }
            });

        this.accountForm = this.formBuilder.group({
            accountNumber: ['', [Validators.required]],
            name: ['', Validators.required],
            calculatedOpeningBalance: [],
            calculatedClosingBalance: [],
        });
        if (this.account.accountNumber !== undefined) {
            this.accountForm.patchValue(this.account);
        }
    }

    get accountNumber(): AbstractControl {
        return this.accountForm.get('accountNumber');
    }
    get name(): AbstractControl {
        return this.accountForm.get('name');
    }
    get calculatedOpeningBalance(): AbstractControl {
        return this.accountForm.get('calculatedOpeningBalance');
    }
    get calculatedClosingBalance(): AbstractControl {
        return this.accountForm.get('calculatedClosingBalance');
    }
    onSaveAccount(): void {
        if (this.accountForm.invalid) {
            this.openInvalidFormWarningDialog();
            return;
        }
        const accountFormValue: Partial<IAccount> = this.accountForm.value as Partial<IAccount>;
        const editedAccount: IAccount = {
            ...this.account,
            ...accountFormValue,
            calculatedOpeningBalance: accountFormValue.calculatedOpeningBalance ?? 0,
            calculatedClosingBalance: accountFormValue.calculatedClosingBalance ?? 0,
            closingBalance: accountFormValue.calculatedClosingBalance ?? 0,
            openingBalance:
                this.hasPreviousData && accountFormValue.calculatedOpeningBalance
                    ? accountFormValue.calculatedOpeningBalance
                    : 0,
        };

        this.saveAccount.emit(editedAccount);
    }

    onCancel(): void {
        if (this.account.accountNumber === undefined) {
            this.cancelAccountCreation.emit();
        } else {
            this.cancelAccountEdit.emit();
        }
    }

    private openInvalidFormWarningDialog(): void {
        // Local
        this.dialog
            .open<ModalComponent, ModalData>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.WARNING,
                    titleLocalizationKey: localizationKeys.aumico_editable_account_invalid_data_modal_title,
                    content: {
                        text: [localizationKeys.aumico_editable_account_invalid_data_modal_content],
                    },
                },
            })
            .afterClosed()
            .pipe(first())
            .subscribe();
    }
}
